/**
 * Quiet Hours soundscape — ambient palettes per category.
 *
 * Built entirely on the `juice/audio.ts` toolbox primitives. No audio
 * files for ambient — everything is synthesized at runtime so the
 * bundle stays small and the soundscape can run forever without
 * looping perceptibly.
 *
 * Architecture per category:
 *
 *  - One or two long-running oscillators tuned to a low fundamental
 *    + a soft consonant interval (major third for morning, minor third
 *    for anxiety, etc).
 *  - Each oscillator's detune slowly drifts via a slow LFO so the
 *    drone never sounds like an identical loop.
 *  - A long bandpass-filtered pink-noise wash, very quiet, sweeping
 *    its cutoff slowly.
 *  - Occasional "ornaments" — a single sine chirp for morning birds,
 *    a tinkling overtone for evening, a low rumble swell for anxiety
 *    rain, a near-silent drift for sleep.
 *
 * The whole field can be started + stopped with `startAmbient(category)`
 * and `stopAmbient()`. The class manages its own node graph and tears
 * down cleanly when the session ends.
 *
 * Mute behaviour: synthesis is bypassed when `audio.isMuted()` is true,
 * but the public API doesn't expose a `playIfNotMuted` wrapper because
 * the ambient is long-running, not an SFX trigger. We check mute at
 * `start()` time; subsequent mute changes ramp the master to zero.
 */

import { audio } from '@/juice';
import type { QuietHoursCategorySlug } from './categories';

type OscWave = 'sine' | 'square' | 'sawtooth' | 'triangle';

interface CategorySoundConfig {
  /** Carrier frequencies (Hz) — drone tones. */
  carriers: number[];
  /** Carrier waveform — sine is the default; triangle for slightly richer. */
  type: OscWave;
  /** Master gain for the carriers (0..1). */
  carrierGain: number;
  /** Noise: bandpass center (Hz), 0 to disable. */
  noiseBp: number;
  /** Noise Q (resonance). */
  noiseQ: number;
  /** Noise gain (0..1). */
  noiseGain: number;
  /** Pink-noise shaping power (>1 reduces highs). */
  noiseShaping: number;
  /** Type of ornament sprinkled occasionally. */
  ornament: 'bird-chirp' | 'wind-chime' | 'low-rumble' | 'sleep-drift' | 'none';
  /** Mean seconds between ornaments. */
  ornamentMeanS: number;
}

const CONFIGS: Record<QuietHoursCategorySlug, CategorySoundConfig> = {
  'morning-calm': {
    // C3 + E3 — warm major third
    carriers: [130.81, 164.81],
    type: 'sine',
    carrierGain: 0.10,
    noiseBp: 1200,
    noiseQ: 0.7,
    noiseGain: 0.025,
    noiseShaping: 1.2,
    ornament: 'bird-chirp',
    ornamentMeanS: 10,
  },
  'evening-wind-down': {
    // A2 + E3 — warm low fifth, wide and grounded
    carriers: [110.0, 164.81],
    type: 'sine',
    carrierGain: 0.11,
    noiseBp: 600,
    noiseQ: 0.6,
    noiseGain: 0.03,
    noiseShaping: 1.5,
    ornament: 'wind-chime',
    ornamentMeanS: 14,
  },
  'anxiety-relief': {
    // D3 + F3 — cool minor third
    carriers: [146.83, 174.61],
    type: 'sine',
    carrierGain: 0.10,
    noiseBp: 1800,
    noiseQ: 1.6,
    noiseGain: 0.045, // softly louder — this is the "rain"
    noiseShaping: 1.0,
    ornament: 'low-rumble',
    ornamentMeanS: 18,
  },
  'sleep-aid': {
    // A2 sub-fundamental only, plus a soft fifth above
    carriers: [110.0, 164.81 / 2 + 27.5], // A2 + low overtone neighbour
    type: 'sine',
    carrierGain: 0.085,
    noiseBp: 200,
    noiseQ: 0.5,
    noiseGain: 0.02,
    noiseShaping: 1.8,
    ornament: 'sleep-drift',
    ornamentMeanS: 22,
  },
};

interface AmbientHandle {
  ctx: AudioContext;
  master: GainNode;
  bus: GainNode;
  carriers: { osc: OscillatorNode; lfo: OscillatorNode; lfoGain: GainNode }[];
  noise?: { src: AudioBufferSourceNode; bp: BiquadFilterNode; sweep: OscillatorNode; sweepGain: GainNode };
  ornamentTimer: ReturnType<typeof setTimeout> | null;
  stopped: boolean;
}

let active: AmbientHandle | null = null;

/**
 * Begin the ambient soundscape for the given category. Idempotent —
 * starting while one is already playing tears the old one down first.
 */
export function startAmbient(category: QuietHoursCategorySlug): void {
  stopAmbient();

  if (audio.isMuted()) {
    // Honor the mute preference. We *could* still build the graph and
    // hold its gain at 0, but skipping entirely is cheaper and the user
    // gets sound back instantly if they unmute mid-session by toggling
    // the preferences bar and tapping a category again.
    return;
  }

  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  audio.unlockAudio();

  const cfg = CONFIGS[category];
  const tNow = ctx.currentTime;
  const fadeIn = 4.0; // a slow 4-second fade-in feels like the room arriving

  // Bus — the whole ambient field is gathered through this gain node so
  // we can fade in/out as one.
  const bus = ctx.createGain();
  bus.gain.cancelScheduledValues(tNow);
  bus.gain.setValueAtTime(0.0001, tNow);
  bus.gain.exponentialRampToValueAtTime(1.0, tNow + fadeIn);
  bus.connect(master);

  // Carriers
  const carriers: AmbientHandle['carriers'] = cfg.carriers.map((freq, i) => {
    const osc = audio.makeOscillator(ctx, { freq, type: cfg.type });

    const g = ctx.createGain();
    g.gain.value = cfg.carrierGain / Math.sqrt(i + 1); // higher carriers slightly quieter

    // LFO modulating detune for slow drift — 0.02–0.05 Hz, amplitude
    // 5–14 cents. The two carriers run different LFO rates so they
    // never line up identically.
    const lfo = audio.makeOscillator(ctx, {
      freq: 0.02 + i * 0.013,
      type: 'sine',
    });
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 7 + i * 4;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.detune);

    osc.connect(g);
    g.connect(bus);
    osc.start(tNow);
    lfo.start(tNow);

    return { osc, lfo, lfoGain };
  });

  // Pink-noise wash
  let noise: AmbientHandle['noise'];
  if (cfg.noiseGain > 0) {
    // 4-second loop of noise, looped indefinitely.
    const buf = audio.noiseBuffer(ctx, { lengthMs: 4000, shaping: cfg.noiseShaping });
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;

    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = cfg.noiseBp;
    bp.Q.value = cfg.noiseQ;

    const ng = ctx.createGain();
    ng.gain.value = cfg.noiseGain;

    // Slow LFO on the filter cutoff — gives the "wind" / "rain" its
    // breath without making it sound like a flange.
    const sweep = audio.makeOscillator(ctx, { freq: 1 / 60, type: 'sine' }); // 60s period
    const sweepGain = ctx.createGain();
    sweepGain.gain.value = cfg.noiseBp * 0.35;
    sweep.connect(sweepGain);
    sweepGain.connect(bp.frequency);

    audio.connectThrough(src, bp, ng, bus);
    src.start(tNow);
    sweep.start(tNow);

    noise = { src, bp, sweep, sweepGain };
  }

  active = {
    ctx,
    master,
    bus,
    carriers,
    ornamentTimer: null,
    stopped: false,
    ...(noise ? { noise } : {}),
  };

  // Schedule the first ornament with a long initial delay so the room
  // settles before anything joins.
  scheduleOrnament(active, cfg, /* initial */ true);
}

/**
 * Stop the ambient soundscape, fading it out gracefully. Subsequent calls
 * are no-ops until `startAmbient` runs again.
 */
export function stopAmbient(): void {
  if (!active) return;
  const a = active;
  active = null;
  a.stopped = true;

  const fadeOut = 1.8;
  const tNow = a.ctx.currentTime;
  try {
    a.bus.gain.cancelScheduledValues(tNow);
    a.bus.gain.setValueAtTime(a.bus.gain.value, tNow);
    a.bus.gain.exponentialRampToValueAtTime(0.0001, tNow + fadeOut);
  } catch {
    /* ignore */
  }

  // Schedule all sources to stop after fadeout.
  const tStop = tNow + fadeOut + 0.1;
  for (const c of a.carriers) {
    try {
      c.osc.stop(tStop);
      c.lfo.stop(tStop);
    } catch {
      /* already stopped */
    }
  }
  if (a.noise) {
    try {
      a.noise.src.stop(tStop);
      a.noise.sweep.stop(tStop);
    } catch {
      /* already stopped */
    }
  }
  if (a.ornamentTimer !== null) {
    clearTimeout(a.ornamentTimer);
  }
}

// ---------------------------------------------------------------------------
// Ornaments — sparse single-shot sounds layered over the drone.
// ---------------------------------------------------------------------------

function scheduleOrnament(
  handle: AmbientHandle,
  cfg: CategorySoundConfig,
  initial: boolean,
): void {
  if (handle.stopped) return;
  if (cfg.ornament === 'none') return;

  // Poisson-ish jitter: delay in [0.5 * mean, 1.5 * mean].
  const base = cfg.ornamentMeanS;
  const min = initial ? base * 1.5 : base * 0.5;
  const max = initial ? base * 2.5 : base * 1.5;
  const delaySec = min + Math.random() * (max - min);

  handle.ornamentTimer = setTimeout(() => {
    if (handle.stopped) return;
    try {
      switch (cfg.ornament) {
        case 'bird-chirp':
          synthBirdChirp(handle);
          break;
        case 'wind-chime':
          synthWindChime(handle);
          break;
        case 'low-rumble':
          synthLowRumble(handle);
          break;
        case 'sleep-drift':
          synthSleepDrift(handle);
          break;
        case 'none':
          break;
      }
    } catch {
      /* ignore — best-effort */
    }
    scheduleOrnament(handle, cfg, false);
  }, delaySec * 1000);
}

function synthBirdChirp(h: AmbientHandle): void {
  const { ctx, bus } = h;
  const t = ctx.currentTime;
  // A short rising sine — soft, distant, never bright.
  const baseFreq = 1400 + Math.random() * 800; // 1.4–2.2 kHz
  const osc = audio.makeOscillator(ctx, { freq: baseFreq, type: 'sine' });
  osc.frequency.exponentialRampToValueAtTime(baseFreq * 1.18, t + 0.18);
  osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.95, t + 0.32);

  const g = ctx.createGain();
  g.gain.cancelScheduledValues(t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.035, t + 0.04);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.34);

  // Tiny lowpass so even at 2kHz it never feels piercing.
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 4000;
  lp.Q.value = 0.4;

  audio.connectThrough(osc, lp, g, bus);
  osc.start(t);
  osc.stop(t + 0.4);
}

function synthWindChime(h: AmbientHandle): void {
  const { ctx, bus } = h;
  const t = ctx.currentTime;
  // Two soft sine partials at high frequencies, gently decaying — like a
  // brass chime caught in slow air.
  const fundamental = 880 + Math.random() * 160; // around A5
  [1, 1.998].forEach((mul, i) => {
    const osc = audio.makeOscillator(ctx, {
      freq: fundamental * mul,
      type: 'sine',
    });
    const g = ctx.createGain();
    g.gain.cancelScheduledValues(t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.025 / (i + 1), t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.005 / (i + 1), t + 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.6);
    audio.connectThrough(osc, g, bus);
    osc.start(t);
    osc.stop(t + 1.8);
  });
}

function synthLowRumble(h: AmbientHandle): void {
  const { ctx, bus } = h;
  const t = ctx.currentTime;
  // A very slow swell of filtered noise + low sine throb — distant thunder
  // without the dramatic crack.
  const buf = audio.noiseBuffer(ctx, { lengthMs: 4000, shaping: 1.4 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 220;
  lp.Q.value = 0.7;
  const g = ctx.createGain();
  g.gain.cancelScheduledValues(t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.035, t + 1.2);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 3.6);
  audio.connectThrough(src, lp, g, bus);
  src.start(t);
  src.stop(t + 4.0);
}

function synthSleepDrift(h: AmbientHandle): void {
  const { ctx, bus } = h;
  const t = ctx.currentTime;
  // A barely-audible single sine at a low octave, slowly fading.
  const osc = audio.makeOscillator(ctx, {
    freq: 73.42, // D2
    type: 'sine',
  });
  const g = ctx.createGain();
  g.gain.cancelScheduledValues(t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.025, t + 1.5);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 5.0);
  audio.connectThrough(osc, g, bus);
  osc.start(t);
  osc.stop(t + 5.2);
}
